import React from 'react';
import { useIntl } from 'react-intl';
import { Button, Badge } from 'reactstrap';
import { StockStatus, StockStatusLabel } from '../models';

const stockStatusConfig = {
  [StockStatus.InStock]: { color: 'success' },
  [StockStatus.LowStock]: { color: 'warning' },
  [StockStatus.OutOfStock]: { color: 'danger' },
};

interface InventorySource {
  id: number;
  name: string;
  kind: 'group' | 'item';
}

interface InventoryItem {
  id: string;
  name: string;
  status: string;
  requiredQty: string;
  currentQty: number | null;
  source?: InventorySource | null;
}

type SourceSelectHandler = (
  source: { id: number; kind: 'group' | 'item' } | null | undefined
) => void;

interface InventorySectionProps {
  items: InventoryItem[];
  onAdd?: () => void;
  onUnlink?: (item: InventoryItem) => void;
  showSource?: boolean;
  hideSourceName?: string;
  onSelectSource?: SourceSelectHandler;
}

const InventorySection: React.FC<InventorySectionProps> = ({
  items,
  onAdd,
  onUnlink,
  showSource,
  hideSourceName,
  onSelectSource,
}) => {
  const intl = useIntl();
  const lowStockCount = items.filter(
    (item) => item.status === StockStatus.LowStock || item.status === StockStatus.OutOfStock
  ).length;

  return (
    <div className="asset-section asset-section--inventory">
      <div className="asset-section__header">
        <div className="asset-section__header-left">
          <span className="asset-section__color-dot asset-section__color-dot--green" />
          <h3 className="asset-section__title">
            {intl.formatMessage({ id: 'assets.sections.inventory' })}
          </h3>
          <Badge color="success" pill>
            {items.length}
          </Badge>
        </div>
        <Button outline color="primary" size="sm" onClick={onAdd}>
          <i className="uil uil-plus mr-1" />
          {intl.formatMessage({ id: 'assets.actions.add' })}
        </Button>
      </div>

      <div className="asset-section__content">
        {items.map((item) => {
          const statusConfig =
            stockStatusConfig[item.status] || stockStatusConfig[StockStatus.InStock];
          return (
            <div key={item.id} className="asset-inventory-card">
              <div className="asset-inventory-card__dot" />
              <div className="asset-inventory-card__info">
                <span className="asset-inventory-card__name">{item.name}</span>
                <span className="asset-inventory-card__required">
                  {intl.formatMessage({ id: 'assets.inventory.requiredQty' })}: {item.requiredQty}
                </span>
                {showSource &&
                  item.source?.name &&
                  (!hideSourceName || item.source.name !== hideSourceName) &&
                  (() => {
                    const src = item.source!;
                    const kindLabel = src.kind === 'group' ? 'Group' : 'Item';
                    const clickable = typeof onSelectSource === 'function';
                    const handleActivate = (): void => {
                      if (!clickable) return;
                      onSelectSource?.({ id: src.id, kind: src.kind });
                    };
                    return (
                      <span
                        className={`asset-section__source-chip asset-section__source-chip--${
                          src.kind
                        }${clickable ? ' asset-section__source-chip--clickable' : ''}`}
                        title={`${kindLabel}: ${src.name}`}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onClick={
                          clickable
                            ? (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleActivate();
                              }
                            : undefined
                        }
                        onKeyDown={
                          clickable
                            ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleActivate();
                                }
                              }
                            : undefined
                        }
                      >
                        <span className="asset-section__source-chip-kind">{kindLabel}</span>
                        <span className="asset-section__source-chip-name">{src.name}</span>
                      </span>
                    );
                  })()}
              </div>
              <Badge color={statusConfig.color} className="asset-inventory-card__status">
                {StockStatusLabel[item.status]}
              </Badge>
              <span className="asset-inventory-card__qty">
                {intl.formatMessage({ id: 'assets.inventory.qty' })}:{' '}
                {item.currentQty !== null ? item.currentQty : '--'}
              </span>
              <button
                className="asset-inventory-card__edit-btn"
                title="Unlink from asset"
                onClick={() => onUnlink?.(item)}
              >
                <i className="uil uil-link-broken" />
              </button>
            </div>
          );
        })}

        {lowStockCount > 0 && (
          <div className="asset-inventory-alert">
            <i className="uil uil-exclamation-triangle mr-1" />
            {intl.formatMessage({ id: 'assets.inventory.lowStockAlert' }, { count: lowStockCount })}
            <a href="#" className="asset-inventory-alert__link">
              {intl.formatMessage({ id: 'assets.inventory.viewDetails' })} {'\u2192'}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventorySection;
